"use client";

import { useEffect, useId, useRef, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import {
  computeNiceYAxisTicks,
  computePriceAxisTicks,
  filterDateRange,
  formatPriceAxisTick,
  indexTo100,
  PEER_COLORS,
  withTimeField,
} from "@/lib/price-chart-utils";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";
import { useDragZoom } from "@/lib/use-drag-zoom";
import type { CompetitorEntry } from "@/lib/competitor-analysis/loader";

// Indexierte Vergleichs-Kurscharts sind wie price-history-chart.tsx tagesgranular.
const MIN_SPAN_MS = 3 * 24 * 60 * 60 * 1000; // 3 Tage

interface ChartRow {
  date: string;
  [seriesKey: string]: string | number | undefined;
}

/** Renn-Animationsdauer in Sekunden - bewusst langsam (Eriks Feedback nach dem ersten,
 * zu schnellen 2,2s-Versuch, dann nochmals "etwas langsamer" nach dem 6s-Zwischenstand),
 * damit der Effekt tatsaechlich als Erzaehlung wahrnehmbar ist. */
const RACE_DURATION_SECONDS = 9;

/** Die Renn-Animation soll nur einmal PRO AKTIE ablaufen (Erik: "beim ersten öffnen
 * einer neuen Aktie ... sollte die Animation doch kommen" - eine anfangs global
 * gescopte Variante des Flags zeigte die Animation faelschlich nur beim allerersten
 * Symbol der Sitzung, nicht bei jeder neu geoeffneten Aktie). `sessionStorage`-Key
 * enthaelt daher das Symbol der Zielaktie - ein Tab-Wechsel weg und zurueck zur
 * GLEICHEN Aktie animiert nicht erneut, eine ANDERE Aktie (neues Symbol) aber schon.
 * Neuer Tab/neues Fenster (neue Sitzung) zeigt den Effekt fuer jede Aktie wieder einmal. */
function raceSeenKey(symbol: string): string {
  return `erikonomie:competitor-race-seen:${symbol}`;
}

/** Sucht per Bisektion die Pfadlaenge, an der ein (monoton in X verlaufender)
 * Linien-Pfad eine bestimmte X-Pixel-Position erreicht. Wird gebraucht, weil SVG nur
 * "Punkt bei Pfadlaenge L" (`getPointAtLength`), nicht "Pfadlaenge bei X-Position"
 * anbietet - fuer eine zeitsynchrone (nicht bogenlaengen-synchrone) Animation muss die
 * Position aber ueber die X-Achse (= Kalenderzeit), nicht die zurueckgelegte Bogenlaenge
 * gesteuert werden. */
function lengthAtX(path: SVGPathElement, targetX: number, totalLength: number): number {
  let lo = 0;
  let hi = totalLength;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (path.getPointAtLength(mid).x < targetX) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * "Konkurrenzanalyse": animierter Kursvergleich als Ersatz fuer die vorherige 3D-Szene
 * (Erik 2026-07-11: "die sieht schlecht aus" - komplett entfernt statt weiter angepasst).
 * Gleiches Kursvergleichs-Muster wie das bestehende Aktien-Duell (peer-compare.tsx: auf
 * 100 indexiert, gemeinsamer Kalenderzeitraum, RangeSelector + Klick-Ziehen-Zoom).
 *
 * Iterationen bis zum finalen Verhalten:
 * 1. Recharts' eingebaute Linien-Animation (`isAnimationActive`) - verworfen: morpht die
 *    Linie aus einer flachen Basislinie in ihre Endform (Werte-Interpolation), keine
 *    echte zeitbasierte Enthuellung, wirkte ruckelig/buggy.
 * 2. Ein gemeinsamer `clip-path`-Wisch-Effekt ueber den GESAMTEN Chart-Container -
 *    verworfen: Achsen/Legende wurden mitverdeckt.
 * 3. Stroke-Dasharray/-Dashoffset (Bogenlaengen-basiert) pro Linie - verworfen: bei
 *    stark schwankenden Kursverlaeufen (z.B. NVIDIA) ist ein Grossteil der
 *    Gesamt-Bogenlaenge in den steilen juengeren Jahren konzentriert, wodurch diese
 *    Aktie ueber weite Teile der Animation sichtbar schneller/langsamer als ruhigere
 *    Kursverlaeufe wirkte (Erik: "NVIDIA laeuft viel schneller als die anderen").
 * 4. **Finale Loesung**: pro Linie ein eigener `<clipPath>` mit wachsendem `<rect>`,
 *    dessen Breite NICHT an der Bogenlaenge, sondern an der X-PIXEL-POSITION (= exakt
 *    dieselbe geteilte Zeitachse fuer alle Serien) haengt - `progress` ist ein
 *    gemeinsamer Bruchteil von `RACE_DURATION_SECONDS`, angewandt auf denselben
 *    Plot-Bereich (`plotLeft`/`plotRight`, aus der Pfadgeometrie der ersten/letzten
 *    Datenpunkte ermittelt, die dank des bereits gemeinsamen Kalenderzeitraums fuer
 *    alle Serien identisch sind). Dadurch enthuellen sich alle Linien garantiert exakt
 *    gleich schnell (gleicher Kalendertag zu jedem Zeitpunkt der Animation), waehrend
 *    jede Linie trotzdem nur ihre eigene Kurvenform zeigt (kein gemeinsamer
 *    Container-Wisch, jede Linie hat ihren eigenen Clip). Firmenlogo-Marker (Erik: "vor
 *    den jeweiligen Charts soll jeweils klein das jeweilige Firmenlogo mitlaufen")
 *    nutzen dieselbe X-Position, per Bisektion (`lengthAtX`) auf die passende
 *    Pfadlaenge/Y-Koordinate der jeweiligen Linie umgerechnet - dadurch laufen alle
 *    Marker garantiert auf gleicher Hoehe (= gleicher Kalendertag) nebeneinander, nur
 *    ihre Y-Position unterscheidet sich je nach Kursverlauf. Alle Serien starten
 *    gleichzeitig und bleiben am Ende ohne Loop stehen; der Clip-Path wird danach
 *    entfernt, damit ein spaeterer Fenster-Resize (neue Pfadgeometrie) nicht an der
 *    alten Clip-Breite haengen bleibt. `useEffect`-Abhaengigkeit auf Range+Zoom (nicht
 *    auf jeden Render) sorgt dafuer, dass die Animation nur bei echtem Datenwechsel neu
 *    abgespielt wird. Laeuft dank `sessionStorage`-Flag (`raceSeenKey()`, pro Symbol)
 *    nur beim ersten Mal je Aktie pro Browser-Sitzung, danach wird der Chart direkt
 *    vollstaendig gezeigt (Erik: "nur beim ersten mal machen und danach direkt
 *    einblenden immer" - pro Aktie, nicht global, siehe Kommentar bei `raceSeenKey`).
 *    Container-Hoehe `h-96` statt `h-64` (Erik: "das ist sonst viel zu klein") - passend
 *    zur Groesse des Haupt-Kurscharts (`price-history-chart.tsx`).
 */
export function CompetitorPriceRaceChart({
  own,
  competitors,
}: {
  own: CompetitorEntry;
  competitors: CompetitorEntry[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const markerRefs = useRef<(HTMLImageElement | null)[]>([]);
  const clipRectRefs = useRef<(SVGRectElement | null)[]>([]);
  const clipIdPrefix = `race-clip-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  // Default 10J statt Max (Erik: die Animation soll keine sehr lange Zeitspanne
  // abbilden, sondern konkret die letzten 10 Jahre bis heute durchlaufen).
  const [range, setRange] = useState<ChartRangeLabel>("10J");
  const isNarrow = useIsNarrowViewport();

  const activeSeries = [
    {
      key: own.symbol,
      label: own.name,
      color: "#16265C",
      logoUrl: own.logoUrl,
      priceHistory: filterDateRange(own.priceHistory, range),
    },
    ...competitors.map((c, i) => ({
      key: c.symbol,
      label: c.name,
      color: PEER_COLORS[i],
      logoUrl: c.logoUrl,
      priceHistory: filterDateRange(c.priceHistory, range),
    })),
  ].filter((s) => s.priceHistory.length > 0);

  let chartData: (ChartRow & { time: number })[] = [];
  if (activeSeries.length > 1) {
    const start = activeSeries.map((s) => s.priceHistory[0].date).reduce((a, b) => (a > b ? a : b));
    const end = activeSeries
      .map((s) => s.priceHistory[s.priceHistory.length - 1].date)
      .reduce((a, b) => (a < b ? a : b));

    const indexedMaps = activeSeries.map((s) =>
      indexTo100(s.priceHistory.filter((p) => p.date >= start && p.date <= end)),
    );
    const allDates = [...new Set(indexedMaps.flatMap((m) => [...m.keys()]))].sort();
    chartData = withTimeField(
      allDates.map((date) => {
        const entry: ChartRow = { date };
        activeSeries.forEach((s, i) => {
          entry[s.key] = indexedMaps[i].get(date);
        });
        return entry;
      }),
    );
  }

  // Mausrad-/Tastatur-Zoom (neu, Siebzehnte autonome Charge) ueber denselben gemeinsamen
  // Hook wie die uebrigen Zeitreihen-Charts - siehe lib/use-drag-zoom.ts. `chartWrapperRef`
  // (vom Hook) UND das bestehende `containerRef` (Renn-Animation-Marker-Positionierung,
  // s.u.) muessen auf denselben <div> zeigen - per gemeinsamer Callback-Ref unten
  // kombiniert statt den Hook um eine "externen Ref entgegennehmen"-Option zu erweitern.
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: chartData[0]?.time,
    fullMax: chartData[chartData.length - 1]?.time,
    minSpan: MIN_SPAN_MS,
  });

  const visibleChartData = zoomRange
    ? chartData.filter((d) => d.time >= zoomRange[0] && d.time <= zoomRange[1])
    : chartData;

  const zoomKey = zoomRange ? zoomRange.join(",") : "full";

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let tickRafId: number | null = null;

    // Recharts/ResponsiveContainer brauchen einen Tick, bis die gemessenen SVG-Pfade
    // tatsaechlich im DOM stehen.
    const setupRaf = requestAnimationFrame(() => {
      // `.recharts-surface` matcht nicht nur die grosse Haupt-Chart-Flaeche, sondern
      // auch die winzigen 14x14px-Icon-SVGs der Legende - das groesste Element (per
      // Flaeche) ist zuverlaessig die eigentliche Chart-Flaeche.
      const svgCandidates = [...container.querySelectorAll<SVGSVGElement>("svg.recharts-surface")];
      const svg = svgCandidates.reduce<SVGSVGElement | null>((largest, candidate) => {
        const area = candidate.clientWidth * candidate.clientHeight;
        const largestArea = largest ? largest.clientWidth * largest.clientHeight : -1;
        return area > largestArea ? candidate : largest;
      }, null);
      const paths = [...container.querySelectorAll<SVGPathElement>(".recharts-line-curve")];
      if (!svg || paths.length === 0) return;

      const svgRect = svg.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offsetX = svgRect.left - containerRect.left;
      const offsetY = svgRect.top - containerRect.top;

      const entries = paths
        .map((path, i) => ({
          path,
          length: path.getTotalLength(),
          marker: markerRefs.current[i] ?? null,
          rect: clipRectRefs.current[i] ?? null,
          origIndex: i,
        }))
        .filter((e) => e.length > 0);
      if (entries.length === 0) return;

      // Gemeinsamer Plot-Bereich (X-Pixel-Range) ueber alle Serien - dank des bereits
      // auf den gemeinsamen Kalenderzeitraum begrenzten `chartData` sollten Start-/
      // Enddatum je Serie praktisch identisch sein, Min/Max ueber alle Serien faengt
      // minimale Rundungsabweichungen ab.
      const startXs = entries.map((e) => e.path.getPointAtLength(0).x);
      const endXs = entries.map((e) => e.path.getPointAtLength(e.length).x);
      const plotLeft = Math.min(...startXs);
      const plotRight = Math.max(...endXs);
      const plotWidth = Math.max(plotRight - plotLeft, 1);

      // Nach dem ersten Mal fuer DIESE Aktie direkt vollstaendig zeigen statt erneut zu
      // animieren (Key ist pro Symbol - eine andere Aktie animiert wieder neu).
      if (sessionStorage.getItem(raceSeenKey(own.symbol)) === "1") {
        entries.forEach(({ path, length, marker }) => {
          path.style.clipPath = "none";
          if (!marker) return;
          const point = path.getPointAtLength(length);
          marker.style.opacity = "1";
          marker.style.transform = `translate(${offsetX + point.x}px, ${offsetY + point.y}px) translate(-50%, -50%)`;
        });
        return;
      }

      entries.forEach(({ path, rect, origIndex }) => {
        path.style.clipPath = `url(#${clipIdPrefix}-${origIndex})`;
        rect?.setAttribute("x", `${plotLeft}`);
        rect?.setAttribute("width", "0");
      });

      const startTime = performance.now();
      function tick(now: number) {
        const progress = Math.min((now - startTime) / (RACE_DURATION_SECONDS * 1000), 1);
        const targetX = plotLeft + progress * plotWidth;
        entries.forEach(({ path, length, marker, rect }) => {
          rect?.setAttribute("width", `${Math.max(targetX - plotLeft, 0)}`);
          if (!marker) return;
          const point = path.getPointAtLength(lengthAtX(path, targetX, length));
          marker.style.opacity = "1";
          marker.style.transform = `translate(${offsetX + point.x}px, ${offsetY + point.y}px) translate(-50%, -50%)`;
        });
        if (progress < 1) {
          tickRafId = requestAnimationFrame(tick);
        } else {
          // Clip-Path entfernen (voll enthuellt) - ein spaeterer Fenster-Resize
          // berechnet die Pfadgeometrie neu, waehrend die alte Clip-Breite stehen
          // bliebe, was zu einer sichtbar abgeschnittenen Linie fuehren wuerde.
          entries.forEach(({ path }) => {
            path.style.clipPath = "none";
          });
          sessionStorage.setItem(raceSeenKey(own.symbol), "1");
        }
      }
      tickRafId = requestAnimationFrame(tick);
    });

    return () => {
      cancelAnimationFrame(setupRaf);
      if (tickRafId !== null) cancelAnimationFrame(tickRafId);
    };
  }, [range, zoomKey, activeSeries.length, clipIdPrefix, own.symbol]);

  const axisTicks = computePriceAxisTicks(visibleChartData, range, isNarrow ? 5 : 8).map((d) => Date.parse(d));
  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = computeNiceYAxisTicks(
    visibleChartData.flatMap((row) => activeSeries.map((s) => row[s.key]).filter((v): v is number => typeof v === "number")),
    false,
  );

  if (chartData.length <= 1) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-ink-secondary">Kursentwicklung im Vergleich</h3>
        <div className="flex items-center gap-3">
          {zoomRange && (
            <button
              type="button"
              onClick={resetZoom}
              className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
            >
              Zoom zurücksetzen
            </button>
          )}
          <RangeSelector
            value={range}
            onChange={(r) => {
              setRange(r);
              resetZoom();
            }}
          />
        </div>
      </div>

      <div
        ref={(el) => {
          containerRef.current = el;
          chartWrapperRef.current = el;
        }}
        className="relative h-96 w-full rounded-lg border border-hairline bg-card p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onKeyDown={onKeyDown}
        {...keyboardA11yProps}
      >
        {activeSeries.map(
          (s, i) =>
            s.logoUrl && (
              // Renn-Marker: Position wird 60x/s per rAF direkt per Inline-Transform
              // gesetzt, next/image waere hier nur Overhead ohne Nutzen. Groesse 28px
              // (Erik: "ein klein wenig groesser") - bleibt deutlich unter der
              // 100x100px-Aufloesung der Logo-Quelle (yahoo-provider.ts), daher weiterhin
              // scharf statt hochskaliert/weich.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={s.key}
                ref={(el) => {
                  markerRefs.current[i] = el;
                }}
                src={s.logoUrl}
                alt=""
                width={100}
                height={100}
                className="pointer-events-none absolute top-0 left-0 z-10 h-7 w-7 rounded-full border border-hairline bg-white object-contain p-1 opacity-0 shadow-sm"
              />
            ),
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={visibleChartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
          >
            <defs>
              {activeSeries.map((s, i) => (
                <clipPath id={`${clipIdPrefix}-${i}`} key={s.key}>
                  <rect
                    ref={(el) => {
                      clipRectRefs.current[i] = el;
                    }}
                    x={0}
                    y={-10000}
                    width={0}
                    height={20000}
                  />
                </clipPath>
              ))}
            </defs>
            <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" />
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              ticks={axisTicks}
              interval={0}
              tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => `${v.toFixed(0)}`}
              width={48}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value, name) => [`${Number(value).toFixed(1)}`, name]}
              labelFormatter={(label) => new Date(Number(label)).toLocaleDateString("de-DE")}
              contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
            />
            <Legend
              formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
            />
            {activeSeries.map((s) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            {refAreaLeft !== null && refAreaRight !== null && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground">
        Kurse indexiert auf 100 am Beginn des gemeinsamen Zeitraums, damit Aktien mit
        unterschiedlichem Kursniveau vergleichbar sind.
      </p>
    </div>
  );
}
